import { Loan } from './types';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import {
  secureRandomInt,
  secureRandomBoolean,
  secureRandomString,
} from 'src/utils/secureRandom';

export interface GetLoansParams {
  startDate?: string;
  endDate?: string;
  cursor?: string;
  limit?: number;
  // Server-side filters (key -> value) derived from grid filter model
  filters?: Record<string, string | number | boolean>;
}

export interface GetLoansResponse {
  success: boolean;
  loans: Loan[];
  nextCursor?: string;
}

const getLoans: QueryFunction<
  GetLoansResponse,
  ['/loans', GetLoansParams]
> = async ({ queryKey }) => {
  const [, params] = queryKey;

  if (isMockServices()) return await mock(getLoansMockData);

  // Build URL with query parameters
  const queryParams: string[] = [];
  if (params.cursor) queryParams.push(`cursor=${params.cursor}`);
  if (params.limit) queryParams.push(`limit=${params.limit}`);
  if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
  if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
  if (params.filters) {
    Object.entries(params.filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        // Assuming backend expects filter[<field>]=<value>
        queryParams.push(
          `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
        );
      }
    });
  }

  const url = `/loans${queryParams.length ? '?' + queryParams.join('&') : ''}`;

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<GetLoansResponse>(url, {
    method: 'GET',
  });

  return {
    success: true,
    loans: response.data?.loans ?? [],
    nextCursor: response.data?.nextCursor,
  };
};

export default getLoans;

export const getLoansMockData: GetLoansResponse = {
  success: true,
  loans: new Array(50).fill(null).map(() => {
    const loanNumber = secureRandomString(10);
    return {
      id: crypto.randomUUID(),
      loanTypeId: secureRandomString(10),
      loanNumber,
      borrowerId: secureRandomString(10),
      status: secureRandomBoolean() ? 'pending' : 'done',
      metadata: {
        loanType: 'HELOC',
        brokerName: 'John Broker',
        countyCode: '600095',
        loanAmount: `${secureRandomInt(10000, 500000)}`,
        loanNumber,
        creditScore: `${secureRandomInt(500, 850)}`,
        loanOfficer: 'Mark Smith',
        loanPurpose: 'Purchase',
        propertyType: 'Single Family',
        borrowerFirstName: 'John',
        borrowerLastName: 'Doe',
        borrowerMiddleName: '',
      },
      isDeleted: false,
      createdAt: '2025-03-26T20:35:57.984Z',
      updatedAt: '2025-03-26T20:35:57.984Z',
    };
  }),
  nextCursor: 'next-cursor',
};
